import { usePack } from "lib/hooks";
import { useState } from "react";
import * as openpgp from 'openpgp';
import { keysClient, packsClient } from "lib/clients";
import { decrypt, decryptSessionKey, encryptSessionKey, expand, useE2EEContext } from "lib/e2ee";
import { InputGroup, Input, FormControl, InputLeftElement, InputRightElement, Tooltip, Button, Icon, Box } from '@chakra-ui/react';
import { Formik, Field, FieldProps, Form } from 'formik';
import { FormAlert, ButtonSubmit } from 'components';
import { object, SchemaOf } from 'yup';
import { validatePassword } from 'lib/utils/inputValidation';
import { PadlockIcon } from 'lib/icons/padlock';
import { AiOutlineEye } from '@react-icons/all-files/ai/AiOutlineEye';
import { AiOutlineEyeInvisible } from '@react-icons/all-files/ai/AiOutlineEyeInvisible';
import { usePackContext } from "./context";

export const validation: SchemaOf<{ password: string }> = object().shape({
  password: validatePassword,
});

export const PromptForOldPassword = () => {
  const [state, setState] = useState<any>({ error: null, success: null, showPassword: false });
  const { packId } = usePackContext();
  const { data: pack, mutate } = usePack(packId);
  const { keys } = useE2EEContext();

  const onSubmit = async ({ password }) => {
    setState({ ...state, error: null, success: null});
    
    try {
      const { data: key } = await keysClient.getKey(pack.key_id);

      const privateKey = await openpgp.decryptKey({
        privateKey: await openpgp.readPrivateKey({ armoredKey: expand(key.private_key_armored) }),
        passphrase: password
      });

      const { data: packs } = await keysClient.getPacks(pack.key_id);

      for (const pack of packs) {
        const sessionKey = await decryptSessionKey(pack.key, privateKey);

        if (pack.id.startsWith('x')) {
          const key = await encryptSessionKey(sessionKey, keys.public);

          await packsClient.updateJoinPack(pack.id, { key, key_id: keys.id })
        }
        else {
          const { data: shareTokens } = await packsClient.getPackShareTokens(pack.id);

          const passwords = [];

          for (const shareToken of shareTokens) {
            passwords.push(await decrypt(shareToken.key, sessionKey));
          }

          const key = await encryptSessionKey(sessionKey, keys.public, passwords);

          await packsClient.updatePack(pack.id, { key, key_id: keys.id })
        }
      }

      mutate();
    }
    catch(e) {
      if (e.message.includes('Incorrect key passphrase')) {
        setState({ ...state, error: 'Incorrect password'});
      }
    }
  }

  const toggleShowPassword = (): void => setState({
    ...state,
    showPassword: !state.showPassword,
  });

  return (
    <Formik
      onSubmit={onSubmit}
      validationSchema={validation}
      initialValues={{ password: '' }}
    >
      <Form style={{ width: '100%' }}>
        <Box marginBottom="20px">
          A password reset was performed on this account so, for security reasons, we can not decrypt this pack. If you recall your previous password, you can enter it here to recover your information.
        </Box>
        <FormAlert error={state.error} success={state.success} />
        <Field key="password" name="password">
          {({ field, meta, form }: FieldProps) => (
            <FormControl
              isInvalid={Boolean(meta.touched && form.errors[field.name])}
              isRequired={true}
            >
              <InputGroup size="md">
                <InputLeftElement height="100%" width="30px" ml="15px">
                  <PadlockIcon />
                </InputLeftElement>
                <Input
                  pr="2rem"
                  pl="4rem"
                  size="lg"
                  placeholder='Enter your old account password'
                  isRequired={true}
                  type={state.showPassword ? "text" : "password"}
                  {...field}
                />
                <InputRightElement mr="15px" height="100%">
                  {state.showPassword ? (
                    <Tooltip label="Hide password">
                      <Button onClick={toggleShowPassword}>
                        <Icon boxSize="24px" as={AiOutlineEye} />
                      </Button>
                    </Tooltip>
                  ) : (
                    <Tooltip label="Show password">
                      <Button colorScheme="brand" onClick={toggleShowPassword}>
                        <Icon boxSize="24px" as={AiOutlineEyeInvisible} />
                      </Button>
                    </Tooltip>
                  )}
                </InputRightElement>
              </InputGroup>
            </FormControl>
          )}
        </Field>
        <Box marginTop="20px">
          <ButtonSubmit
            name="login"
            size="xl"
            isFullWidth={true}
          >
            Recover my pack
          </ButtonSubmit>
        </Box>
      </Form>
    </Formik>
  )
}